import { createRoute } from '@tanstack/react-router';
import { Copy, Search } from 'lucide-react';
import { Shell } from '../components/layout/Shell';
import { DuplicateReviewPanel } from '../components/inventory/DuplicateReviewPanel';
import { useDuplicateScan, useDismissDuplicate, useMergeDuplicates } from '../hooks/useDuplicates';
import { useTranslation } from '../i18n';
import { Route as rootRoute } from './__root';
import styles from './duplicates.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/duplicates',
  component: DuplicatesPage,
});

function DuplicatesPage() {
  const { t } = useTranslation();
  const { data, isLoading, isFetching, refetch } = useDuplicateScan();
  const dismiss = useDismissDuplicate();
  const merge = useMergeDuplicates();

  const pairs = data?.data ?? [];
  const hasScanned = data !== undefined;

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            <Copy size={20} strokeWidth={1.75} />
            {t('duplicates.title')}
          </h1>
          <button
            className={styles.scanBtn}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <Search size={14} strokeWidth={2} />
            {isFetching ? t('duplicates.scanning') : t('duplicates.scan')}
          </button>
        </div>

        {!hasScanned && !isLoading ? (
          <div className={styles.empty}>
            <Copy size={48} strokeWidth={1} />
            <div className={styles.emptyTitle}>{t('duplicates.scanPromptTitle')}</div>
            <div className={styles.emptyDesc}>{t('duplicates.scanPromptDesc')}</div>
          </div>
        ) : isLoading || isFetching ? (
          <div className={styles.loading}>{t('duplicates.scanning')}</div>
        ) : pairs.length === 0 ? (
          <div className={styles.empty}>
            <Copy size={48} strokeWidth={1} />
            <div className={styles.emptyTitle}>{t('duplicates.noneFound')}</div>
            <div className={styles.emptyDesc}>{t('duplicates.noneFoundDesc')}</div>
          </div>
        ) : (
          <div className={styles.list}>
            <div className={styles.count}>
              {pairs.length} {t('duplicates.pairsFound')}
            </div>
            {pairs.map((pair, i) => (
              <DuplicateReviewPanel
                key={`${pair.item1.id}-${pair.item2.id}`}
                item1={pair.item1}
                item2={pair.item2}
                similarity={pair.similarity}
                onDismiss={(id1, id2) => dismiss.mutate({ item1Id: id1, item2Id: id2 })}
                onMerge={(keepId, removeId) => merge.mutate({ keepItemId: keepId, removeItemId: removeId })}
                isPending={dismiss.isPending || merge.isPending}
              />
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
